import { useRouter } from 'next/router';
import { memo, useCallback, useEffect } from 'react';

import { ExternalLinkIcon } from '@air/design-icons';
import { FileStatusIconCompleted, FileStatusSubtitle, FileStatusTitle } from '@air/feature-file-status/ui';

import { getTaskPaneFileStatusIconSize } from '~/components/FileStatusTrackingPane/getTaskPaneFileStatusIconSize';
import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { TaskPaneActionButton } from '~/components/FileStatusTrackingPane/TaskPaneActionButton';
import { TaskPaneDismissButton } from '~/components/FileStatusTrackingPane/TaskPaneDismissButton';
import { createAllContentAssetRoute, Routes } from '~/constants/routes';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { type CTWTask, type GetCompletedMetadata } from '~/store/tasks/types';
import { isCypressRun } from '~/utils/PathUtils';

export type CopyToWorkspaceCompletedPanelItemProps = {
  metadata: Pick<GetCompletedMetadata<CTWTask>, 'duplicatedRootBoards' | 'duplicatedRootClips' | 'targetWorkspaceId'>;
  onClear: () => void;
};

export const CopyToWorkspaceCompletedPanelItem = memo(
  ({
    metadata: { duplicatedRootBoards, duplicatedRootClips, targetWorkspaceId },
    onClear,
  }: CopyToWorkspaceCompletedPanelItemProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const { push } = useRouter();
    const copiedBoard = !!duplicatedRootBoards?.length;
    const itemId = duplicatedRootBoards?.[0]?.id || duplicatedRootClips?.[0]?.id;

    const workspaceUrl = itemId
      ? // biome-ignore lint/style/noNestedTernary: PLA-1724 — pre-existing debt, not introduced by the icon migration
        copiedBoard
        ? `/b/${itemId}`
        : createAllContentAssetRoute(itemId)
      : `${Routes.media.all}?wsid=${targetWorkspaceId}`;

    const handleView = useCallback(() => {
      void push(workspaceUrl);
    }, [push, workspaceUrl]);

    useEffect(() => {
      if (!isCypressRun()) {
        setTimeout(() => {
          window.open(workspaceUrl, '_blank');
          // biome-ignore lint/style/noMagicNumbers: PLA-1724 — pre-existing debt, not introduced by the icon migration
        }, 2000);
      }
    }, [workspaceUrl]);

    return (
      <PaneItemTW
        avatar={<FileStatusIconCompleted size={getTaskPaneFileStatusIconSize(ui2026)} />}
        title={
          <FileStatusTitle data-testid="COPY_TO_WORKSPACE_COMPLETED_TITLE">
            {ui2026 ? 'Copied to workspace' : 'Save complete!'}
          </FileStatusTitle>
        }
        subtitle={
          ui2026 ? undefined : (
            <FileStatusSubtitle className="truncate">
              {
                <>
                  {`If you weren't redirected, `}
                  <button
                    data-testid="COPY_TO_WORKSPACE_COMPLETED_OPEN_BOARD_BUTTON"
                    data-copied-item-id={itemId}
                    className="ads:text-accent text-12 text-blue-9 ads:hover:text-accent-hover hover:text-blue-11 hover:underline"
                    onClick={handleView}
                    type="button"
                  >
                    click here
                  </button>
                </>
              }
            </FileStatusSubtitle>
          )
        }
        buttons={
          <>
            {ui2026 ? (
              <TaskPaneActionButton
                data-copied-item-id={itemId}
                data-testid="COPY_TO_WORKSPACE_COMPLETED_OPEN_BOARD_BUTTON"
                icon={<ExternalLinkIcon size="sm" />}
                onClick={handleView}
              >
                View
              </TaskPaneActionButton>
            ) : null}
            <TaskPaneDismissButton label="Clear" onClick={onClear} />
          </>
        }
      />
    );
  },
);

CopyToWorkspaceCompletedPanelItem.displayName = 'CopyToWorkspaceCompletedPanelItem';
